import { AWS_ACCOUNT_PATTERN } from "@p0security/integrations-aws-shared/constants";
import type { ElementConfigOf } from "@p0security/shared/install/types";
import { isKeyOf } from "@p0security/util/types/is";

import {
  RepositoryAccessHosting,
  RepositoryAccessVault,
} from "./components.ts";

type Hosting = ElementConfigOf<typeof RepositoryAccessHosting>;
type VaultConfig = ElementConfigOf<typeof RepositoryAccessVault>;

// The fields that place the connector, or the key's secret, on AWS: all but
// the type itself.
type HostingField = Exclude<keyof Extract<Hosting, { type: "aws" }>, "type">;
type VaultField = Exclude<
  keyof Extract<VaultConfig, { type: "aws-sm" }>,
  "type"
>;

/** A connector name that works for everything the connector's Terraform names
 * after it: a Lambda function with its ECR repository and IAM role. ECR is why
 * a name has at least two characters and no two hyphens in a row. */
export const CONNECTOR_NAME_PATTERN = /^[a-z](?:-?[a-z0-9])+$/;

/** The longest connector name: Lambda and IAM cap a function or role name at
 * 64 characters. */
const CONNECTOR_NAME_MAX_LENGTH = 64;

/** An AWS region, such as `us-west-2`. */
export const AWS_REGION_PATTERN = /^[a-z]+(?:-[a-z]+)+-\d+$/;

/** A region outside AWS's commercial partition: GovCloud, China, the ISO
 * regions (`us-iso-`, `us-isob-`, `eu-isoe-`, `us-isof-`) or the European
 * Sovereign Cloud. P0 invokes the connector, and points it at its secret, by
 * `arn:aws:` ARNs, so neither the connector nor its secret can be in one. */
export const AWS_OTHER_PARTITION_REGION_PATTERN =
  /^(?:us-gov-|cn-|[a-z]+-iso[a-z]*-|eusc-)/;

export const UNSUPPORTED_AWS_PARTITION =
  "GitHub Repositories doesn't support AWS GovCloud or China regions yet.";

/** A field the connector can't be deployed with, and what to enter instead. */
export type InvalidField = { field: string; message: string };

/** A rule for a field's value, and what to enter when it's broken. */
type Rule = { message: string; test: (value: string) => boolean };

const connectorNameRules: readonly Rule[] = [
  {
    message: `Use 2 to ${CONNECTOR_NAME_MAX_LENGTH} characters for the connector name: lowercase letters, digits and single hyphens, starting with a letter and ending with a letter or digit.`,
    test: (name) =>
      CONNECTOR_NAME_PATTERN.test(name) &&
      name.length <= CONNECTOR_NAME_MAX_LENGTH,
  },
];

const awsAccountRules = (subject: string): readonly Rule[] => [
  {
    message: `Enter the 12-digit ID of the AWS account ${subject}.`,
    test: (account) => AWS_ACCOUNT_PATTERN.test(account),
  },
];

const awsRegionRules = (subject: string): readonly Rule[] => [
  {
    message: `Enter the AWS region ${subject}, such as us-west-2.`,
    test: (region) => AWS_REGION_PATTERN.test(region),
  },
  {
    message: UNSUPPORTED_AWS_PARTITION,
    test: (region) => !AWS_OTHER_PARTITION_REGION_PATTERN.test(region),
  },
];

// Only AWS has rules: GitHub Repositories refuses a Google vault or hosting
// before these are checked.
const HOSTING_RULES: {
  readonly aws: Readonly<Record<HostingField, readonly Rule[]>>;
} = {
  aws: {
    accountId: awsAccountRules("the connector runs in"),
    connectorName: connectorNameRules,
    connectorRegion: awsRegionRules("the connector runs in"),
  },
};

const VAULT_RULES: {
  readonly "aws-sm": Readonly<Record<VaultField, readonly Rule[]>>;
} = {
  "aws-sm": {
    install: awsAccountRules("the private key's secret is in"),
    secretsRegion: awsRegionRules("the private key's secret is in"),
  },
};

/** The first field of `config`, at `path`, that breaks one of its `rules`.
 *
 * Each value is checked trimmed, as the install framework stores it. A value
 * that's missing or isn't a string breaks its field's first rule.
 */
const findInvalidField = (
  path: string,
  config: Readonly<Record<string, unknown>>,
  rules: Readonly<Record<string, readonly Rule[]>>
): InvalidField | undefined => {
  for (const [field, fieldRules] of Object.entries(rules)) {
    const value = config[field];
    const broken =
      typeof value === "string"
        ? fieldRules.find(({ test }) => !test(value.trim()))
        : fieldRules[0];
    if (broken) return { field: `${path}.${field}`, message: broken.message };
  }
  return undefined;
};

/** The first field of `hosting` that P0's connector Terraform can't deploy
 * with, or `undefined` when there's none.
 *
 * A hosting without a type is checked as the element's default. Only an AWS
 * hosting is checked: Google hosting is refused before this runs, and a type
 * P0 doesn't know is left to the install framework, which refuses it.
 */
export const findInvalidHostingField = (
  hosting: Readonly<Record<string, unknown>>
): InvalidField | undefined => {
  const type = hosting.type ?? RepositoryAccessHosting.default;
  return isKeyOf(HOSTING_RULES, type)
    ? findInvalidField("hosting", hosting, HOSTING_RULES[type])
    : undefined;
};

/** The first field of `vault` that P0's connector Terraform can't deploy
 * with, or `undefined` when there's none. It treats the vault's type as
 * {@link findInvalidHostingField} treats the hosting's. */
export const findInvalidVaultField = (
  vault: Readonly<Record<string, unknown>>
): InvalidField | undefined => {
  const type = vault.type ?? RepositoryAccessVault.default;
  return isKeyOf(VAULT_RULES, type)
    ? findInvalidField("vault", vault, VAULT_RULES[type])
    : undefined;
};
